// View Pitches V2 — the field readers of the pitch card, in one place.
//
// Every function here answers ONE question about a pitch payload, and every one
// of them returns `null` when the answer is not in the payload. That is the
// contract the V2 card is built on: a missing value renders NOTHING — no row,
// no dash, no zero. The old card printed "-" for absence, which is how an empty
// transfer fee read as a free transfer and an unknown height read as a measured
// one.
//
// The payload itself is untouched foundation: same endpoint
// (GET /dashboard/club/tender-pitches/), same field names. What changed is
// WHICH fields are read — the inventory (BESTANDSAUFNAHME 7.7) proved three of
// them were being read off the wrong object:
//
//   * position   — `player_position` and `tender_position` BOTH carry
//                  `tender.position` (dashboard/serializers.py:1222-1240), so
//                  every card showed the position the CLUB asked for, never the
//                  one the player plays. `player.position` is the real one and
//                  PlayerMinimalSerializer has always sent it.
//   * club       — `pitch.club` is `ClubMinimalSerializer(source='tender.club')`
//                  (serializers.py:1060): the REQUESTING club, i.e. the reader's
//                  own. The player's club is `player.club_name` /
//                  `player.club_image` / `player.club_league`.
//   * market value — `player.market_value` + `player.market_value_currency`
//                  have always been served and were never rendered.
//
// Additive fields from the parallel backend work are read defensively here
// (`nationality`, `club_league`, `time_remaining_hours`, `document_filename`,
// `pitcher_age`, `pitcher_licence`, `pitcher_experience_years`): absent means
// null means nothing is drawn, so old and new payloads both render honestly.

import {
  formatCurrencyWithSymbol,
  formatTransferFee,
} from "../../utils/currencyFormatter";

/** A staff/coach request. Same discriminator the old card used. */
export const isStaffTender = (tender) => tender?.tender_category === "staff";

/** Up to two initials for a square avatar placeholder. */
export const initialsOf = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

/**
 * An amount, written out in full ("€12,500,000"), or null.
 *
 * Never abbreviated and never rounded to a shorter unit: an abbreviated
 * transfer fee is the single most expensive place on this surface to be
 * approximately right. `formatCurrencyWithSymbol` is the platform's own
 * formatter and stays the one source of the symbol table.
 */
export const formatAmount = (value, currencyCode) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return formatCurrencyWithSymbol(numeric, currencyCode);
};

/**
 * The transfer fee of a pitch, or null when there is none.
 *
 * Zero counts as "none": a fee of nothing is not a price, and printing "€0"
 * would state one that was never asked.
 *
 * Formatting goes through `formatTransferFee`, the pitch-specific formatter
 * added on develop (#284), NOT through a local currency map. That helper is
 * where the two rules for a fee live: a row with no stored currency reads as
 * EUR (the platform default for a pitch fee), and a legacy INR row still
 * renders with ₹ instead of losing its identity to a wrong symbol.
 */
export const transferFeeOf = (pitch) => {
  const raw = pitch?.transfer_fee;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return formatTransferFee(numeric, pitch?.transfer_fee_currency);
};

/**
 * The free-agent flag, and ONLY the flag.
 *
 * `free_agent` is READ, never re-derived. Since develop #284 it is a resolved
 * server-side verdict (`agent_pitch_logic.resolve_free_agent`) rather than the
 * old guess "no transfer fee", and the card must not put a second guess beside
 * it: the V1 fallback `!transfer_fee` is what put the badge on every staff
 * response and on any player whose agent simply left the amount blank
 * (inventory 7.7 defect 1).
 *
 * The `!isStaffTender` guard is not a re-derivation and can never turn a false
 * into a true. It restates the FIRST rule of the resolver itself ("a staff
 * pitch is never a free agent") on the display side, because "the badge never
 * appears on a staff or coach card" is a named requirement of this rebuild and
 * a legacy row carrying a stale stored flag must not be able to break it.
 */
export const isFreeAgentPitch = (pitch, tender) =>
  pitch?.free_agent === true && !isStaffTender(tender);

/** The player's OWN position, not the request's. */
export const playerPositionOf = (pitch) => pitch?.player?.position || null;

/** The position the request asks for — used for the staff/coach role line. */
export const requestPositionOf = (pitch) =>
  pitch?.player_position || pitch?.tender_position || null;

/** The player's club, with the league the additive serializer field carries. */
export const playerClubOf = (pitch) => {
  const name = pitch?.player?.club_name || null;
  const image = pitch?.player?.club_image || null;
  const league = pitch?.player?.club_league || null;
  if (!name && !image && !league) return null;
  return { name, image, league };
};

/**
 * The nationality, normalised onto the shape the PLATFORM's flag helpers read.
 *
 * The additive serializer field is `{name, alpha2, alpha3, flag_url}`;
 * `utils/countryFlag` resolves `code`/`iso`/`country_code`/`iso2` and
 * `FlagImg`/`flagImageUrl` prefer `image_url`. Renaming the two keys here means
 * the card uses the platform's existing image-flag component unchanged instead
 * of a second flag system (`components/agent-tender/FlagImg.jsx`, in use on 15
 * surfaces). Returns null when there is no nationality, so nothing is drawn.
 */
const normaliseNationality = (nat) => {
  if (!nat || !nat.name) return null;
  return {
    ...nat,
    code: nat.code || nat.alpha2 || nat.alpha3 || null,
    image_url: nat.image_url || nat.flag_url || null,
  };
};

export const nationalityOf = (pitch) =>
  normaliseNationality(pitch?.player?.nationality || pitch?.nationality || null);

/**
 * ALL citizenships of the pitched player (Marin 2026-11-02, lead decision L-FE-1):
 * the serializer's `nationalities[]` (TheSports order, index 0 = `nationality`),
 * each normalised like `nationalityOf`; payloads without the list fall back to
 * the single `nationality`. De-duplicated on name, so a repeated country draws
 * one flag. Empty list when nothing is known — the card then draws nothing.
 */
export const nationalitiesOf = (pitch) => {
  const source = pitch?.player || pitch || {};
  const raw = Array.isArray(source.nationalities) && source.nationalities.length
    ? source.nationalities
    : [source.nationality];
  const seen = new Set();
  return raw.map(normaliseNationality).filter((nat) => {
    if (!nat) return false;
    const key = nat.name.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Right / Left / Both, or null. Accepts the backend's single letters. */
export const footOf = (pitch) => {
  const raw = pitch?.player?.foot;
  if (!raw) return null;
  const value = String(raw).trim().toUpperCase();
  if (value === "R" || value === "RIGHT") return "Right";
  if (value === "L" || value === "LEFT") return "Left";
  if (value === "B" || value === "BOTH" || value === "A") return "Both";
  return String(raw);
};

/** The age shown on the card: the player's, or the staff member's. */
export const ageOf = (pitch, tender) => {
  const raw = isStaffTender(tender) ? pitch?.pitcher_age : pitch?.player?.age;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const heightOf = (pitch) => {
  const raw = pitch?.player?.height;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const marketValueOf = (pitch) => {
  const raw = pitch?.player?.market_value;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const experienceYearsOf = (pitch) => {
  const raw = pitch?.pitcher_experience_years;
  if (raw === null || raw === undefined || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const licenceOf = (pitch) => pitch?.pitcher_licence || null;

/**
 * The name of the attached document, or null.
 *
 * `document_name` is a backend CONSTANT ("Staff Contract" / "Player Contract",
 * serializers.py:1232) and says nothing about the file, so it is not a
 * filename. `document_filename` is the additive field that carries the real
 * one; without it the row is simply not drawn.
 */
export const documentNameOf = (pitch) => pitch?.document_filename || null;

/**
 * How long the club still has, as the short value beside "Time left to
 * response:".
 *
 * `time_remaining_hours` is the additive integer field, already clamped at zero
 * by the backend. `time_left` is the legacy STRING and it can go negative
 * (serializers.py:1212 — `f"{hours} hrs"` with no floor), which is how a card
 * ends up advertising "-3 hrs" left. Either way a value at or below zero means
 * the clock is done and nothing is shown: an expired pitch says so with its own
 * banner, not with a negative countdown.
 */
export const timeLeftOf = (pitch) => {
  const hours = pitch?.time_remaining_hours;
  if (Number.isFinite(hours)) {
    if (hours <= 0) return null;
    return `${hours} ${hours === 1 ? "hr" : "hrs"}`;
  }
  const legacy = pitch?.time_left;
  if (typeof legacy !== "string" || legacy.trim() === "") return null;
  const parsed = parseFloat(legacy);
  if (Number.isFinite(parsed) && parsed <= 0) return null;
  return legacy;
};

/**
 * Where the card's profile link points, as `{ path, label }`, or null when the
 * payload carries no identifier the route can resolve.
 *
 * ONE reader for all three kinds, because the three routes resolve three
 * DIFFERENT identifiers and feeding one the other's id does not open the wrong
 * profile, it opens a 404:
 *   /search/player/:playerId — Wyscout id, else the platform player id.
 *                              Unchanged; this is what the V1 card already did.
 *   /search/coach/:coachId   — the TheSports coach id
 *                              (users/urls.py:94, `<str:ts_coach_id>`).
 *   /search/staff/:staffId   — the ACCOUNT id
 *                              (users/urls.py:92, `<int:account_id>`).
 *
 * Coach is asked BEFORE staff, exactly the order the platform's own StaffCard
 * decides it in (components/club/StaffCard.js:151-157): a pitcher who carries a
 * `ts_coach_id` is a coach, and the coach profile is the richer record.
 *
 * On a staff/coach request the pitcher travels as `created_by`, and
 * CreatedBySerializer sends `{name, first_name, last_name, email}` — NEITHER
 * id. So today this returns null for those requests and no link is drawn, which
 * is the whole point of returning null: a link assembled from a substitute
 * field would take the club to somebody else's profile. The moment the pitch
 * payload carries `created_by.ts_coach_id` or `created_by.account_id`, the link
 * appears with no further change here.
 */
export const profileLinkOf = (pitch, tender) => {
  if (isStaffTender(tender)) {
    const pitcher = pitch?.created_by || null;
    const coachId = pitcher?.ts_coach_id;
    if (coachId) {
      return { path: `/search/coach/${coachId}`, label: "Coach profile" };
    }
    const accountId = pitcher?.account_id;
    if (accountId) {
      return { path: `/search/staff/${accountId}`, label: "Staff profile" };
    }
    return null;
  }
  const playerId = pitch?.player?.wyscout_id || pitch?.player?.id;
  if (!playerId) return null;
  return { path: `/search/player/${playerId}`, label: "Player profile" };
};

/**
 * The profile of the agent who pitched, or null.
 *
 * `/search/agent/:agentId` resolves an ACCOUNT id
 * (dashboard/agents/public/<account_id>/), and `applied_by_agent.account_id` is
 * exactly that — AgentMinimalSerializer has always sent it. Null whenever it is
 * absent, e.g. on a pitch a staff member submitted for themselves, where there
 * is no agent at all.
 */
export const agentProfileLinkOf = (pitch) => {
  const accountId = pitch?.applied_by_agent?.account_id;
  if (accountId === null || accountId === undefined || accountId === "") {
    return null;
  }
  return `/search/agent/${accountId}`;
};

/** The agent's note. Served in `content`, never rendered before. */
export const agentNoteOf = (pitch) => {
  const note = pitch?.content;
  if (typeof note !== "string" || note.trim() === "") return null;
  return note.trim();
};

/** "24 years" / "1 year", or null. */
export const formatYears = (value) => {
  if (value === null || value === undefined) return null;
  return `${value} ${Number(value) === 1 ? "year" : "years"}`;
};

/** The six status buckets. The ids ARE the `status` query parameter. */
export const PITCH_TABS = [
  { id: "active", label: "Pitches received" },
  { id: "scouting", label: "Scouting" },
  { id: "accepted", label: "Pitches accepted" },
  { id: "rejected", label: "Pitches rejected" },
  { id: "expired", label: "Pitches expired" },
  { id: "deal", label: "Deals" },
];

/**
 * The tab counters, exactly as the old strip computed them — including the
 * subtraction fallback, because a bucket missing from that subtraction is
 * silently counted as still-undecided. `pitches_deal` (singular) is gone: the
 * backend only ever sends `pitches_deals` (inventory 7.7 defect 12).
 */
export const tabCount = (tender, tabId) => {
  switch (tabId) {
    case "active":
      return (
        tender?.active_pitches ||
        tender?.total_pitches -
          tender?.pitches_accepted -
          tender?.pitches_rejected -
          tender?.pitches_expired -
          tender?.pitches_deals -
          (tender?.pitches_scouting || 0) ||
        0
      );
    case "scouting":
      return tender?.pitches_scouting || 0;
    case "accepted":
      return tender?.pitches_accepted || 0;
    case "rejected":
      return tender?.pitches_rejected || 0;
    case "expired":
      return tender?.pitches_expired || 0;
    case "deal":
      return tender?.pitches_deals || 0;
    default:
      return 0;
  }
};

/** Left accent, tab pill and status dot, one static class string per bucket. */
export const STATUS_TONE = {
  active: {
    accent: "border-l-[#2183FE]",
    pill: "bg-[#2183FE] text-white border-[#2183FE]",
    dot: "bg-[#2183FE]",
    activeDot: "bg-white",
  },
  scouting: {
    accent: "border-l-[#F59E0B]",
    pill: "bg-[#F59E0B] text-white border-[#F59E0B]",
    dot: "bg-[#F59E0B]",
    activeDot: "bg-white",
  },
  accepted: {
    accent: "border-l-[#009669]",
    pill: "bg-[#009669] text-white border-[#009669]",
    dot: "bg-[#009669]",
    activeDot: "bg-white",
  },
  rejected: {
    accent: "border-l-[#DC2626]",
    pill: "bg-[#DC2626] text-white border-[#DC2626]",
    dot: "bg-[#DC2626]",
    activeDot: "bg-white",
  },
  expired: {
    accent: "border-l-[#6B7280]",
    pill: "bg-[#6B7280] text-white border-[#6B7280]",
    dot: "bg-[#6B7280]",
    activeDot: "bg-white",
  },
  deal: {
    accent: "border-l-[#7C3AED]",
    pill: "bg-[#7C3AED] text-white border-[#7C3AED]",
    dot: "bg-[#7C3AED]",
    activeDot: "bg-white",
  },
};

/** `signed` and `external_deal` live in the Deals bucket, as they do server-side. */
export const bucketOf = (status) =>
  status === "signed" || status === "external_deal" ? "deal" : status;
